/* eslint-disable max-lines-per-function */
import { useEffect, useState } from 'react';

import type { ClientModel } from '@/domain/models/client.js';
import type { BusinessProcessResponse, CreateBusinessProcessParams, ProjectDocResponse } from '@/domain/models/project.js';
import type { ClientController } from '@/presentation/controllers/client-controller.js';
import type { ProjectController } from '@/presentation/controllers/project-controller.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import { buildCreateProjectParams, buildUpdateProjectParams } from '@/presentation/pages/projects/project-form/utils/build-save-params.js';
import { EMPTY_FORM, type FormState } from '@/presentation/pages/projects/project-form/utils/form-state.js';
import { validateProjectForm } from '@/presentation/pages/projects/project-form/utils/validate-project-form.js';
import type { ProjectStatus } from '@/shared/utils/format-status.js';

interface UseProjectFormParams {
    controller: ProjectController;
    clientController: ClientController;
    mode: 'create' | 'edit';
    projectId?: string;
    open: boolean;
    onSaved: () => void;
    onClose: () => void;
}

export function useProjectForm({ controller, clientController, mode, projectId, open, onSaved, onClose }: UseProjectFormParams) {
    const { translate } = useApp();
    const isEdit = mode === 'edit';

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [clients, setClients] = useState<ClientModel[]>([]);
    const [selectedClient, setSelectedClient] = useState<ClientModel | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [projectStatus, setProjectStatus] = useState<ProjectStatus>('ATIVO');
    const [statusSaving, setStatusSaving] = useState(false);
    const [processes, setProcesses] = useState<BusinessProcessResponse[]>([]);
    const [documents, setDocuments] = useState<ProjectDocResponse[]>([]);

    useEffect(() => {
        if (!open) {
            return;
        }
        let isMounted = true;
        setForm(EMPTY_FORM);
        setErrors({});
        setSelectedClient(null);
        setProcesses([]);
        setDocuments([]);
        setProjectStatus('ATIVO');

        if (isEdit && projectId) {
            controller.listBusinessProcesses(projectId).then((result) => {
                if (!isMounted) {
                    return;
                }
                result.fold(
                    () => {},
                    (list) => setProcesses(list)
                );
            });
            controller.listDocuments(projectId).then((result) => {
                if (!isMounted) {
                    return;
                }
                result.fold(
                    () => {},
                    (list) => setDocuments(list)
                );
            });
        }

        clientController
            .loadClients()
            .then((result) => {
                if (!isMounted) {
                    return;
                }
                result.fold(
                    () => {},
                    (list) => setClients(list)
                );
            })
            .catch(() => {});

        if (isEdit && projectId) {
            setLoading(true);
            controller.loadProjectById(projectId).then((result) => {
                if (!isMounted) {
                    return;
                }
                result.fold(
                    () => {},
                    (project) => {
                        setForm({
                            clientId: project.clientId,
                            name: project.name,
                            abbreviation: project.abbreviation,
                            description: project.description ?? '',
                            clientPmName: project.clientPmName ?? '',
                            clientPmEmail: project.clientPmEmail ?? '',
                            clientPmPhone: project.clientPmPhone ?? '',
                            agilePmName: project.agilePmName ?? '',
                            agilePmEmail: project.agilePmEmail ?? '',
                            agilePmPhone: project.agilePmPhone ?? '',
                            overrideSlaEstimateEf: project.slaHoursEstimateEf !== null,
                            slaHoursEstimateEf: project.slaHoursEstimateEf !== null ? String(project.slaHoursEstimateEf) : '',
                            overrideSlaStartDev: project.slaHoursStartDev !== null,
                            slaHoursStartDev: project.slaHoursStartDev !== null ? String(project.slaHoursStartDev) : '',
                            overrideSlaAnalyzeBug: project.slaHoursAnalyzeBug !== null,
                            slaHoursAnalyzeBug: project.slaHoursAnalyzeBug !== null ? String(project.slaHoursAnalyzeBug) : '',
                            overrideSlaAnalyzeMinor: project.slaHoursAnalyzeMinor !== null,
                            slaHoursAnalyzeMinor: project.slaHoursAnalyzeMinor !== null ? String(project.slaHoursAnalyzeMinor) : '',
                            overrideSlaDaysValidateDoc: project.slaDaysValidateDoc !== null,
                            slaDaysValidateDoc: project.slaDaysValidateDoc !== null ? String(project.slaDaysValidateDoc) : '',
                            overrideSlaMinScoreAi: project.slaMinScoreAi !== null,
                            slaMinScoreAi: project.slaMinScoreAi !== null ? String(project.slaMinScoreAi) : '',
                            overrideAiStrictnessLevel: project.aiStrictnessLevel !== null,
                            aiStrictnessLevel: project.aiStrictnessLevel !== null ? String(project.aiStrictnessLevel) : '',
                            overrideAiExtraSystemPrompt: project.aiExtraSystemPrompt !== null,
                            aiExtraSystemPrompt: project.aiExtraSystemPrompt ?? '',
                            overrideAiModel: project.aiModel !== null,
                            aiModel: project.aiModel ?? '',
                            jiraProjectKey: project.jiraProjectKey ?? '',
                            jiraBaseUrl: project.jiraBaseUrl ?? '',
                            reportsUrl: project.reportsUrl ?? ''
                        });
                        setProjectStatus(project.status);
                    }
                );
                setLoading(false);
            });
        }
        return () => {
            isMounted = false;
        };
    }, [open, isEdit, projectId, controller, clientController]);

    useEffect(() => {
        if (form.clientId && clients.length > 0) {
            setSelectedClient(clients.find((c) => c.id === form.clientId) ?? null);
        }
    }, [form.clientId, clients]);

    const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: '' }));
    };

    const handleClientChange = (clientId: string) => {
        setField('clientId', clientId);
        const client = clients.find((c) => c.id === clientId) ?? null;
        setSelectedClient(client);
    };

    const validate = (): boolean => {
        const errs = validateProjectForm(form, isEdit, {
            clientRequired: translate('projects.errors.clientRequired'),
            nameRequired: translate('projects.errors.nameRequired'),
            abbrRequired: translate('projects.errors.abbrRequired'),
            abbrLength: translate('projects.errors.abbrLength'),
            invalidEmail: translate('projects.errors.invalidEmail'),
            invalidUrl: translate('projects.links.invalidUrl')
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            return;
        }
        setSaving(true);
        if (isEdit && projectId) {
            const params = buildUpdateProjectParams(form);
            const result = await controller.updateProject(projectId, params);
            setSaving(false);
            if (result.isRight()) {
                onSaved();
                onClose();
            }
        } else {
            const params = buildCreateProjectParams(form);
            const result = await controller.createProject(params);
            setSaving(false);
            if (result.isRight()) {
                onSaved();
                onClose();
            }
        }
    };

    const toggleProjectStatus = async () => {
        if (!projectId) {
            return;
        }
        const nextStatus: ProjectStatus = projectStatus === 'ATIVO' ? 'INATIVO' : 'ATIVO';
        setStatusSaving(true);
        const result = await controller.updateProjectStatus(projectId, nextStatus);
        setStatusSaving(false);
        if (result.isRight()) {
            setProjectStatus(nextStatus);
            onSaved();
        }
    };

    const saveBusinessProcess = async (params: CreateBusinessProcessParams, operationMode: 'add' | 'edit', processId?: string): Promise<boolean> => {
        if (!projectId) {
            return false;
        }
        if (operationMode === 'add') {
            const result = await controller.createBusinessProcess(projectId, params);
            if (result.isRight()) {
                setProcesses((prev) => [...prev, result.value]);
                return true;
            }
            return false;
        }
        if (processId) {
            const result = await controller.updateBusinessProcess(projectId, processId, params);
            if (result.isRight()) {
                setProcesses((prev) => prev.map((p) => (p.id === result.value.id ? result.value : p)));
                return true;
            }
            return false;
        }
        return false;
    };

    const toggleProcessActive = async (process: BusinessProcessResponse) => {
        if (!projectId) {
            return;
        }
        const result = await controller.toggleBusinessProcess(projectId, process.id, !process.isActive);
        if (result.isRight()) {
            setProcesses((prev) => prev.map((p) => (p.id === process.id ? { ...p, isActive: !p.isActive } : p)));
        }
    };

    const deleteBusinessProcess = async (processId: string): Promise<boolean> => {
        if (!projectId) {
            return false;
        }
        const result = await controller.deleteBusinessProcess(projectId, processId);
        if (result.isRight()) {
            setProcesses((prev) => prev.filter((p) => p.id !== processId));
            return true;
        }
        return false;
    };

    const uploadDocument = async (category: string, file: File): Promise<boolean> => {
        if (!projectId) {
            return false;
        }
        const result = await controller.uploadDocument(projectId, { category, file });
        if (result.isRight()) {
            setDocuments((prev) => [...prev.filter((d) => d.category !== category), result.value]);
            return true;
        }
        return false;
    };

    const downloadDocument = async (doc: ProjectDocResponse) => {
        if (!projectId) {
            return;
        }
        const result = await controller.getDocumentDownloadUrl(projectId, doc.id);
        if (result.isRight()) {
            window.open(result.value.url, '_blank', 'noopener,noreferrer');
        }
    };

    const deleteDocument = async (docId: string): Promise<boolean> => {
        if (!projectId) {
            return false;
        }
        const result = await controller.deleteDocument(projectId, docId);
        if (result.isRight()) {
            setDocuments((prev) => prev.filter((d) => d.id !== docId));
            return true;
        }
        return false;
    };

    return {
        loading,
        saving,
        form,
        clients,
        selectedClient,
        errors,
        projectStatus,
        statusSaving,
        processes,
        documents,
        setField,
        handleClientChange,
        handleSave,
        toggleProjectStatus,
        saveBusinessProcess,
        toggleProcessActive,
        deleteBusinessProcess,
        uploadDocument,
        downloadDocument,
        deleteDocument
    };
}
